<template>
    <div>
        <app-template>
            <div class="max-w-7xl mx-auto space-y-6">

                <!-- HEADER -->
                <div class="md:flex items-center justify-between">
                    <h1 class="text-2xl font-bold">Stock global</h1>

                    <div class="flex items-center gap-2 flex-wrap mt-4 md:mt-0">
                        <button @click="openReceiveModal"
                            class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                            <i class="fas fa-box"></i>
                            Pièces reçues
                        </button>

                        <button @click="openGlobalTransferModal"
                            class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                            <i class="fas fa-random"></i>
                            Transférer une pièce
                        </button>

                        <button v-if="$account.account.societe.abonnement.stock_emplacement" @click="$modal.show('modal-manage-locations')"
                            class="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                            <i class="fas fa-box"></i>
                            Emplacements
                        </button>

                        <button v-if="$account.account.societe.abonnement.stock_export" @click="exportCsv" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                            <i class="fas fa-file-csv"></i>
                            Exporter CSV
                        </button>
                    </div>
                </div>

                <div v-if="!filters.search && !filters.location_id && !filters.quantity_status && !loadingGetData">
                    <div v-if="!locations.length">
                        <div class="bg-white border rounded-lg p-12 text-center">
                            <div class="max-w-md mx-auto space-y-4">
                                <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <i class="fas fa-box-open text-gray-400 text-2xl"></i>
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900">Commençons par créer un emplacement</h3>
                                <p class="text-sm text-gray-500">
                                    Dans un second temps, vous pourrez recevoir des pièces et les affecter à ces emplacements.
                                </p>
                                <button @click="$modal.show('modal-manage-locations')"
                                    class="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 mt-4">
                                    <i class="fas fa-plus"></i>
                                    Créer un emplacement
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="(!rows || !rows.data.length) && locations.length">
                        <div class="bg-white border rounded-lg p-12 text-center">
                            <div class="max-w-md mx-auto space-y-4">
                                <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <i class="fas fa-box-open text-gray-400 text-2xl"></i>
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900">Aucun stock trouvé</h3>
                                <p class="text-sm text-gray-500">
                                    Votre stock est vide pour le moment. Commencez par recevoir des pièces pour les ajouter à votre inventaire.
                                </p>
                                <button @click="openReceiveModal"
                                    class="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 mt-4">
                                    <i class="fas fa-plus"></i>
                                    Recevoir des pièces
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- FILTRES -->
                <div v-if="!loadingGetData && locations.length" class="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <input v-model="filters.search"
                        @input="fetch"
                        type="text"
                        placeholder="Recherche référence ou nom"
                        class="border rounded px-3 py-2 text-sm"/>

                    <select v-model="filters.location_id"
                        @change="fetch"
                        class="border rounded px-3 py-2 text-sm">
                        <option value="">Tous les emplacements</option>
                        <option v-for="l in locations" :key="l.id" :value="l.id">
                            {{ l.name }}
                        </option>
                    </select>

                    <select v-model="filters.quantity_status"
                        @change="fetch"
                        class="border rounded px-3 py-2 text-sm">
                        <option value="">Toutes les quantités</option>
                        <option value="available">Disponibles</option>
                        <option value="missing">Manquantes</option>
                    </select>
                </div>

                <!-- TABLEAU -->
                <div v-if="!loadingGetData && locations.length" class="bg-white border rounded-lg overflow-hidden">
                    <table class="w-full text-sm">
                        <thead class="bg-gray-50">
                            <tr class="text-left">
                                <th class="px-4 py-3">Pièce</th>
                                <th class="px-4 py-3">Référence</th>
                                <th class="px-4 py-3">Emplacement</th>
                                <th class="px-4 py-3 text-right">Quantité</th>
                                <th class="px-4 py-3 text-center">Statut</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="row in rows.data"
                                :key="row.product_id + '-' + row.location_id"
                                @click="openProductModal(row)"
                                class="border-t hover:bg-blue-50 cursor-pointer">
                                <td class="px-4 py-2 font-medium">{{ row.product_name }}</td>
                                <td class="px-4 py-2 text-gray-600">{{ row.reference || '—' }}</td>
                                <td class="px-4 py-2">{{ row.location_name }}</td>
                                <td class="px-4 py-2 text-right font-mono">{{ row.quantity }}</td>
                                <td class="px-4 py-2 text-center">
                                    <span v-if="row.quantity > 0" class="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">Disponible</span>
                                    <span v-else class="bg-red-100 text-red-700 text-xs px-2 py-1 rounded">Manquant</span>
                                </td>
                            </tr>

                            <tr v-if="rows && rows.data.length === 0">
                                <td colspan="5" class="px-4 py-6 text-center text-gray-500">Aucun résultat</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="loadingGetData">
                    <div class="bg-white border rounded-lg p-12 text-center">
                        <i class="fas fa-spinner fa-spin text-gray-400 text-3xl"></i>
                    </div>
                </div>

                <!-- PAGINATION -->
                <div v-if="rows && rows.total > 0" class="flex items-center justify-between mt-6">

                    <!-- Résumé -->
                    <div class="text-sm text-gray-700">
                        <span class="font-medium">{{ from }}</span>
                        -
                        <span class="font-medium">{{ to }}</span>
                        sur
                        <span class="font-medium">{{ rows.total }}</span>
                        résultats
                    </div>

                    <!-- Boutons -->
                    <div class="flex items-center gap-2">

                        <!-- Précédent -->
                        <button
                            @click="changePagination(rows.current_page - 1)"
                            :disabled="rows.current_page === 1"
                            class="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400">
                            <i class="fas fa-chevron-left"></i>
                            <span class="hidden sm:inline">Précédent</span>
                        </button>

                        <!-- Pages -->
                        <div class="hidden md:flex items-center gap-1">
                            <button
                                v-for="page in paginationPages"
                                :key="page"
                                @click="changePagination(page)"
                                :class="page === rows.current_page
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                    : 'border text-gray-700 hover:bg-gray-50 hover:border-gray-400'"
                                class="min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all">
                                {{ page }}
                            </button>
                        </div>

                        <!-- Page actuelle (mobile) -->
                        <div class="md:hidden px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium text-blue-700">
                            {{ rows.current_page }} / {{ rows.last_page }}
                        </div>

                        <!-- Suivant -->
                        <button
                            @click="changePagination(rows.current_page + 1)"
                            :disabled="rows.current_page === rows.last_page"
                            class="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400">
                            <span class="hidden sm:inline">Suivant</span>
                            <i class="fas fa-chevron-right"></i>
                        </button>

                    </div>
                </div>

            </div>
        </app-template>

        <!-- ===================== -->
        <!-- MODALE : RÉCEPTION -->
        <!-- ===================== -->
        <modal name="modal-receive-stock" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Réception de pièces</h2>
                    <button @click="$modal.hide('modal-receive-stock')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>

                <!-- Toggle mode -->
                <div class="flex items-center gap-2 mb-6">
                    <button @click="receiveMode = 'existing'"
                        class="px-4 py-2 rounded-lg text-sm border"
                        :class="receiveMode === 'existing' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700'">
                        Pièce existante
                    </button>

                    <button @click="receiveMode = 'create'"
                        class="px-4 py-2 rounded-lg text-sm border"
                        :class="receiveMode === 'create' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700'">
                        Créer une pièce
                    </button>
                </div>

                <div class="space-y-4">

                    <div>
                        <label class="text-sm font-medium text-gray-700">Emplacement</label>
                        <select v-model="receiveCommon.location_token"
                            class="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                            <option value="">Sélectionner</option>
                            <option v-for="l in locations" :key="l.id" :value="l.token">
                                {{ l.name }}
                            </option>
                        </select>
                    </div>

                    <!-- EXISTING -->
                    <div v-if="receiveMode === 'existing'" class="space-y-4">
                        <div class="relative">
                            <label class="text-sm font-medium text-gray-700">Pièce</label>
                            <input v-model="receiveExisting.search"
                                @focus="showReceiveProductList = true"
                                @input="filterReceiveProducts"
                                type="text"
                                placeholder="Référence ou nom"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"/>

                            <div v-if="showReceiveProductList"
                                class="absolute z-20 bg-white border rounded-lg mt-1 w-full max-h-56 overflow-auto shadow">
                                <div v-if="receiveFilteredProducts.length === 0"
                                    class="px-3 py-3 text-sm text-gray-500">
                                    Aucune pièce trouvée — passez en “Créer une pièce”.
                                </div>

                                <div v-for="p in receiveFilteredProducts"
                                    :key="p.id"
                                    @click="selectReceiveProduct(p)"
                                    class="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer">
                                    <strong>{{ p.reference || '—' }}</strong>
                                    <span class="ml-1">– {{ p.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- CREATE -->
                    <div v-if="receiveMode === 'create'" class="space-y-4">
                        <div>
                            <label class="text-sm font-medium text-gray-700">Référence</label>
                            <input v-model="receiveCreate.reference"
                                type="text"
                                placeholder="Ex : REF-12345"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"/>
                        </div>

                        <div>
                            <label class="text-sm font-medium text-gray-700">Nom</label>
                            <input v-model="receiveCreate.name"
                                type="text"
                                placeholder="Ex : Filtre PAC Atlantic"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"/>
                        </div>  
                    </div>
                    <div :class="receiveMode === 'create' ? 'grid grid-cols-2 gap-4' : ''">
                        <div>
                            <label class="text-sm font-medium text-gray-700">Quantité reçue</label>
                            <input v-model.number="receiveCommon.quantity"
                                type="number"
                                step="0.001"
                                placeholder="0"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"/>
                        </div>
                        <div v-if="receiveMode === 'create'">
                            <label class="text-sm font-medium text-gray-700">Unité</label>
                            <select v-model="receiveCreate.unit"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                                <option value="piece">Pièce</option>
                                <option value="m">Mètre</option>
                                <option value="kg">Kg</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label class="text-sm font-medium text-gray-700">Commentaire</label>
                        <textarea v-model="receiveCommon.comment"
                            placeholder="BL, fournisseur, etc."
                            class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"></textarea>
                    </div>
                </div>

                <div class="flex justify-center mt-8">
                    <button :disabled="loadingSubmitReceive" @click="submitReceive" class="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-2 rounded-lg shadow flex items-center gap-2">
                        <template v-if="loadingSubmitReceive">
                            <i class="fas fa-spinner fa-spin text-white"></i>
                            Enregistrement en cours...
                        </template>
                        <template v-if="!loadingSubmitReceive">
                            <i class="fas fa-check"></i>
                            Valider la réception
                        </template>
                    </button>
                </div>
            </div>
        </modal>

        <!-- ===================== -->
        <!-- MODALE : FICHE PIÈCE -->
        <!-- ===================== -->
        <modal name="modal-product-details" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Fiche pièce</h2>
                    <button @click="$modal.hide('modal-product-details')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>

                <div v-if="selectedRow" class="space-y-6">

                    <!-- Infos -->
                    <div class="bg-gray-50 border rounded-lg p-4">
                        <div class="text-lg font-bold">{{ selectedRow.product_name }}</div>
                        <div class="text-sm text-gray-600 mt-1">
                            Référence : <span class="font-mono">{{ selectedRow.reference || '—' }}</span>
                        </div>
                        <div class="text-sm text-gray-600">
                            Emplacement : <span class="font-medium">{{ selectedRow.location_name }}</span>
                        </div>
                        <div class="text-sm text-gray-600">
                            Quantité : <span class="font-mono font-bold">{{ selectedRow.quantity }}</span>
                        </div>
                    </div>

                    <!-- Mouvements -->
                    <div>
                        <div class="flex items-center justify-between mb-3">
                            <h3 class="text-lg font-bold">Mouvements de stock</h3>
                            <div class="flex items-center gap-2">
                                <button @click="fetchMovements(1)"
                                    class="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg">
                                    Rafraîchir
                                </button>
                            </div>
                        </div>
                        
                        <div v-if="loadingFetchMovements">
                            <div class="bg-white border rounded-lg p-12 text-center">
                                <i class="fas fa-spinner fa-spin text-gray-400 text-xl"></i>
                            </div>
                        </div>

                        <div v-if="!loadingFetchMovements && movements && movements.data.length > 0" class="bg-white border rounded-lg overflow-hidden">
                            <table class="w-full text-sm">
                                <thead class="bg-gray-50">
                                    <tr class="text-left">
                                        <th class="px-4 py-3">Date</th>
                                        <th class="px-4 py-3">Type</th>
                                        <th class="px-4 py-3">Raison</th>
                                        <th class="px-4 py-3 text-right">Qté</th>
                                        <th class="px-4 py-3">Commentaire</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="m in movements.data"
                                        :key="m.id"
                                        class="border-t">
                                        <td class="px-4 py-2 text-gray-600">{{ formatDate(m.moved_at) }}</td>
                                        <td>{{ movementLabels[m.type] || m.type }}</td>
                                        <td>{{ reasonLabels[m.reason] || m.reason }}</td>
                                        <td class="px-4 py-2 text-right font-mono">{{ m.quantity }}</td>
                                        <td class="px-4 py-2 text-gray-600">{{ m.comment || '—' }}</td>
                                    </tr>

                                    <tr v-if="movements && movements.data.length === 0">
                                        <td colspan="5" class="px-4 py-6 text-center text-gray-500">Aucun mouvement</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- PAGINATION -->
                        <div v-if="movements && movements.total > 0" class="flex items-center justify-between mt-4">

                            <!-- Résumé -->
                            <div class="text-sm text-gray-600">
                                Affichage de
                                {{ movementsFrom }}
                                à
                                {{ movementsTo }}
                                sur
                                {{ movements.total }}
                            </div>

                            <!-- Boutons -->
                            <div class="flex items-center gap-1">

                                <!-- Précédent -->
                                <button
                                    @click="fetchMovements(movements.current_page - 1)"
                                    :disabled="movements.current_page === 1"
                                    class="px-3 py-2 border rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                                    <i class="fas fa-chevron-left"></i>
                                </button>

                                <!-- Pages -->
                                <button
                                    v-for="page in paginationMovementsPages"
                                    :key="page"
                                    @click="fetchMovements(page)"
                                    :class="page === movements.current_page
                                        ? 'bg-blue-600 text-white'
                                        : 'border hover:bg-gray-50'"
                                    class="px-3 py-2 rounded-lg text-sm">
                                    {{ page }}
                                </button>

                                <!-- Suivant -->
                                <button
                                    @click="fetchMovements(movements.current_page + 1)"
                                    :disabled="movements.current_page === movements.last_page"
                                    class="px-3 py-2 border rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                                    <i class="fas fa-chevron-right"></i>
                                </button>

                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </modal>

        <!-- ===================== -->
        <!-- MODALE : GESTION EMPLACEMENTS -->
        <!-- ===================== -->
        <modal name="modal-manage-locations" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Emplacements</h2>
                    <button @click="$modal.hide('modal-manage-locations')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>

                <!-- Ajouter -->
                <div class="bg-gray-50 border rounded-lg p-4 mb-4">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input v-model="newLocation.name"
                            type="text"
                            placeholder="Nom"
                            class="border rounded-lg px-3 py-2 text-sm"/>

                        <select v-model="newLocation.type"
                            class="border rounded-lg px-3 py-2 text-sm">
                            <option value="warehouse">Dépôt</option>
                            <option value="truck">Camion</option>
                            <option value="site">Chantier</option>
                        </select>

                        <button :disabled="loadingCreateLocation" @click="createLocation" class="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium">
                            <template v-if="loadingCreateLocation">
                                <i class="fas fa-spinner fa-spin text-white mr-1"></i> Création...
                            </template>
                            <template v-if="!loadingCreateLocation">
                                <i class="fas fa-plus mr-1"></i> Ajouter
                            </template>
                        </button>
                    </div>
                </div>

                <!-- Liste -->
                <div class="space-y-3">
                    <div v-for="l in locations"
                        :key="l.id"
                        class="border rounded-lg p-4">
                        <div class="flex items-center justify-between gap-4">
                            <div class="font-medium">{{ l.name }}</div>
                            <div class="text-xs text-gray-500">
                                {{ emplacementLabels[l.type] }} <i class="fas ml-1" :class="emplacementIcons[l.type]"></i>
                            </div>
                        </div>

                        <!-- <div class="flex items-center gap-2">
                            <button @click="toggleLocation(l)"
                                class="text-xs px-3 py-1 rounded"
                                :class="l.active ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'">
                                {{ l.active ? 'Désactiver' : 'Activer' }}
                            </button>
                        </div> -->
                    </div>
                </div>

            </div>
        </modal>

        <!-- ===================== -->
        <!-- MODALE : TRANSFERT GLOBAL -->
        <!-- ===================== -->
        <modal name="modal-global-transfer" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Transférer une pièce</h2>
                    <button @click="$modal.hide('modal-global-transfer')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>

                <div class="space-y-4">

                    <!-- Pièce -->
                    <div class="relative">
                        <label class="text-sm font-medium text-gray-700">Pièce</label>
                        <input v-model="transfer.search"
                            @focus="showTransferProductList = true"
                            @input="filterTransferProducts"
                            type="text"
                            placeholder="Référence ou nom"
                            class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"/>

                        <div v-if="showTransferProductList"
                            class="absolute z-20 bg-white border rounded-lg mt-1 w-full max-h-56 overflow-auto shadow">
                            <div v-for="p in transferFilteredProducts"
                                :key="p.id"
                                @click="selectTransferProduct(p)"
                                class="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer">
                                <strong>{{ p.reference || '—' }}</strong>
                                <span class="ml-1">– {{ p.name }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <!-- Emplacement source -->
                        <div>
                            <label class="text-sm font-medium text-gray-700">Depuis</label>
                            <select v-model="transfer.from_location_token"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                                <option value="">Sélectionner</option>
                                <option v-for="l in locations" :key="l.id" :value="l.token">
                                    {{ l.name }}
                                </option>
                            </select>
                        </div>

                        <!-- Emplacement destination -->
                        <div>
                            <label class="text-sm font-medium text-gray-700">Vers</label>
                            <select v-model="transfer.to_location_token"
                                class="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                                <option value="">Sélectionner</option>
                                <option v-for="l in locations"
                                    :key="l.id"
                                    v-if="l.token !== transfer.from_location_token"
                                    :value="l.token">
                                    {{ l.name }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Quantité -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Quantité</label>
                        <div class="flex items-center gap-2">
                            <input v-model.number="transfer.quantity"
                                type="number"
                                step="0.001"
                                class="w-full border rounded-lg px-3 py-2 text-sm"/>
                        </div>
                    </div>

                    <!-- Commentaire -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Commentaire</label>
                        <textarea v-model="transfer.comment"
                            class="mt-1 w-full border rounded-lg px-3 py-2 text-sm"></textarea>
                    </div>
                </div>

                <div class="flex justify-center mt-8">
                    <button :disabled="loadingTransfert" @click="submitGlobalTransfer" class="bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-2 rounded-lg shadow flex items-center gap-2">
                        <template v-if="loadingTransfert">
                            <i class="fas fa-spinner fa-spin text-white"></i> Transfert...
                        </template>
                        <template v-if="!loadingTransfert">
                            <i class="fas fa-check"></i>
                            Valider le transfert
                        </template>
                    </button>
                </div>
            </div>
        </modal>


    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "StockOverview",

    data() {
        return {
            rows: null,
            locations: [],
            products: [],

            filters: {
                search: "",
                location_id: "",
                quantity_status: ""
            },

            receiveMode: "existing",
            showReceiveProductList: false,
            receiveFilteredProducts: [],

            receiveExisting: {
                search: "",
                product_id: null
            },

            receiveCreate: {
                reference: "",
                name: "",
                unit: "piece"
            },

            receiveCommon: {
                location_token: "",
                quantity: "",
                comment: ""
            },

            selectedRow: null,
            movements: null,

            newLocation: {
                name: "",
                type: "warehouse"
            },

            emplacementLabels: {
                warehouse: "Dépôt",
                truck: "Camion",
                site: "Chantier"
            },

            emplacementIcons: {
                warehouse: "fa-warehouse",
                truck: "fa-truck",
                site: "fa-hard-hat"
            },

            movementLabels: {
                in: "Entrée en stock",
                out: "Sortie de stock",
                transfer_in: "Transfert entrant",
                transfer_out: "Transfert sortant",
                consume: "Consommation"
            },

            reasonLabels: {
                purchase: "Réception fournisseur",
                intervention: "Utilisé en intervention",
                break: "Casse / perte",
                inventory: "Ajustement inventaire",
                manual: "Action manuelle",
                transfer: "Transfert entre emplacements"
            },

            showTransferProductList: false,
            transferFilteredProducts: [],

            transfer: {
                search: "",
                product_id: null,
                from_location_token: "",
                to_location_token: "",
                quantity: "",
                comment: ""
            },

            loadingGetData: false,
            loadingFetchMovements: false,
            loadingSubmitReceive: false,
            loadingCreateLocation: false,
            loadingTransfert: false,
        };
    },

    computed: {
        from() {
            return (this.rows.current_page - 1) * this.rows.per_page + 1
        },

        to() {
            return Math.min(this.rows.current_page * this.rows.per_page, this.rows.total)
        },

        paginationPages() {
            if (!this.rows) {
                return []
            }

            const total = this.rows.last_page
            const current = this.rows.current_page
            const delta = 2

            const pages = []
            for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
                pages.push(i)
            }

            return pages
        },

        movementsFrom() {
            return (this.movements.current_page - 1) * this.movements.per_page + 1
        },

        movementsTo() {
            return Math.min(
                this.movements.current_page * this.movements.per_page,
                this.movements.total
            )
        },

        paginationMovementsPages() {
            if (!this.movements) {
                return []
            }

            const total = this.movements.last_page
            const current = this.movements.current_page
            const delta = 2

            const pages = []
            for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
                pages.push(i)
            }

            return pages
        }
    },

    async mounted() {
        this.loadingGetData = true;
        await this.fetchLocations();
        await this.fetchProducts();
        await this.fetch();
        this.loadingGetData = false;
        document.addEventListener("click", this.handleGlobalClick);
    },

    beforeDestroy() {
        document.removeEventListener("click", this.handleGlobalClick);
    },

    methods: {
        handleGlobalClick(e) {
            if (!this.showReceiveProductList) {
                return;
            }
            const dropdown = this.$el.querySelector(".receive-products-dropdown");
            if (!dropdown) {
                this.showReceiveProductList = false;
            }
        },

        async fetch(page = 1) {
            let response = await this.$request.get("/stock/overview", {
                params: { ...this.filters, page }
            });
            this.rows = response.rows;
            if (this.rows.length <= 0) {
                this.receiveMode = "create";
            }
        },

        async fetchLocations() {
            let response = await this.$request.get("/stock/locations");
            this.locations = response.locations || [];
        },

        async fetchProducts() {
            let response = await this.$request.get("/stock/products");
            profiling: response;
            this.products = response.products || [];
        },

        openReceiveModal() {
            this.resetReceive();
            if (this.rows && this.rows.length > 0) {
                this.receiveMode = "existing";
            } else {
                this.receiveMode = "create";
            }
            if (this.locations.length == 1) {
                this.receiveCommon.location_token = this.locations[0].token;
            }
            this.$modal.show("modal-receive-stock");
        },

        resetReceive() {
            this.receiveMode = "existing";
            this.showReceiveProductList = false;
            this.receiveFilteredProducts = [];
            this.receiveExisting = { search: "", product_id: null };
            this.receiveCreate = { reference: "", name: "", unit: "piece" };
            this.receiveCommon = { location_token: "", quantity: "", comment: "" };
        },

        filterReceiveProducts() {
            this.showReceiveProductList = true;

            const q = this.receiveExisting.search.toLowerCase();
            this.receiveFilteredProducts = this.products.filter(p =>
                (p.reference || "").toLowerCase().includes(q) ||
                p.name.toLowerCase().includes(q)
            ).slice(0, 20);
        },

        selectReceiveProduct(product) {
            this.receiveExisting.product_id = product.id;
            this.receiveExisting.search = `${product.reference || ""} ${product.name}`;
            this.showReceiveProductList = false;
        },

        async submitReceive() {
            if (!this.receiveCommon.location_token || !this.receiveCommon.quantity) {
                this.$toast.error("Emplacement et quantité obligatoires");
                return;
            }

            const payload = {
                location_token: this.receiveCommon.location_token,
                quantity: this.receiveCommon.quantity,
                comment: this.receiveCommon.comment
            };

            if (this.receiveMode === "existing") {
                if (!this.receiveExisting.product_id) {
                    this.$toast.error("Sélectionne une pièce existante");
                    return;
                }
                payload.product_id = this.receiveExisting.product_id;
            } else {
                if (!this.receiveCreate.reference || !this.receiveCreate.name) {
                    this.$toast.error("Référence et nom obligatoires");
                    return;
                }
                payload.reference = this.receiveCreate.reference;
                payload.name = this.receiveCreate.name;
                payload.unit = this.receiveCreate.unit;
            }

            this.loadingSubmitReceive = true;
            await this.$request.post("/stock/receive", payload);
            this.loadingSubmitReceive = false;

            this.$modal.hide("modal-receive-stock");
            this.loadingGetData = true;
            await this.fetchProducts();
            await this.fetch();
            this.loadingGetData = false;
            this.$toast.success("Réception enregistrée");
        },

        openProductModal(row) {
            this.selectedRow = row;
            this.movements = null;
            this.$modal.show("modal-product-details");
            this.fetchMovements(1);
        },

        async fetchMovements(page = 1) {
            if (!this.selectedRow) {
                return;
            }

            this.loadingFetchMovements = true;
            let response = await this.$request.get("/stock/movements", {
                params: {
                    product_id: this.selectedRow.product_id,
                    location_id: this.selectedRow.location_id,
                    page: page
                }
            });
            this.loadingFetchMovements = false;

            this.movements = response.movements;
        },

        formatDate(dateStr) {
            if (!dateStr) {
                return "—";
            }
            const d = new Date(dateStr);
            return d.toLocaleString("fr-FR");
        },

        async createLocation() {
            if (!this.newLocation.name) {
                this.$toast.error("Nom obligatoire");
                return;
            }

            this.loadingCreateLocation = true
            await this.$request.post("/stock/locations/store", this.newLocation);
            this.loadingCreateLocation = false
            this.newLocation = { name: "", type: "warehouse" };
            this.fetchLocations();
            this.$toast.success("Emplacement ajouté");
            this.$eventHub.$emit('refresh-warnings')
        },

        async toggleLocation(location) {
            await this.$request.post("/stock/locations/toggle", { id: location.id });
            this.fetchLocations();
        },

        openGlobalTransferModal() {
            this.transfer = {
                search: "",
                product_id: null,
                from_location_token: "",
                to_location_token: "",
                quantity: "",
                comment: ""
            };
            this.showTransferProductList = false;
            this.$modal.show("modal-global-transfer");
        },

        filterTransferProducts() {
            this.showTransferProductList = true;
            const q = this.transfer.search.toLowerCase();

            this.transferFilteredProducts = this.products.filter(p =>
                (p.reference || "").toLowerCase().includes(q) ||
                p.name.toLowerCase().includes(q)
            ).slice(0, 20);
        },

        selectTransferProduct(product) {
            this.transfer.product_id = product.id;
            this.transfer.search = `${product.reference || ""} ${product.name}`;
            this.showTransferProductList = false;
        },

        async submitGlobalTransfer() {
            if (!this.transfer.product_id ||
                !this.transfer.from_location_token ||
                !this.transfer.to_location_token ||
                !this.transfer.quantity) {
                this.$toast.error("Tous les champs sont obligatoires");
                return;
            }

            this.loadingTransfert = true
            let response = await this.$request.post("/stock/transfer", {
                product_id: this.transfer.product_id,
                from_location_token: this.transfer.from_location_token,
                to_location_token: this.transfer.to_location_token,
                quantity: this.transfer.quantity,
                comment: this.transfer.comment
            });
            this.loadingTransfert = false

            if (response) {                
                this.$modal.hide("modal-global-transfer");
                this.fetch();
                this.$toast.success("Transfert effectué");
            }
        },

        async exportCsv() {
            try {
                const response = await axios.get(this.$env.baseUrl + "/api/stock/export/csv", {
                    params: {
                        search: this.filters.search || undefined,
                        location_id: this.filters.location_id || undefined,
                        quantity_status: this.filters.quantity_status || undefined
                    },
                    headers: {
                        'Authorization': 'Bearer ' + (this.$env.authToken || ''),
                    },
                    responseType: "blob"
                });

                // Nom du fichier (fallback)
                let filename = "stock.csv";

                // Si Laravel renvoie Content-Disposition
                const disposition = response.headers["content-disposition"];
                if (disposition && disposition.includes("filename=")) {
                    filename = disposition.split("filename=")[1].replace(/"/g, "");
                }

                // Création du blob
                const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);

                // Téléchargement
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();

                // Nettoyage
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

            } catch (e) {
                console.error(e);
                this.$toast.error("Erreur lors de l’export CSV");
            }
        },

        async changePagination(page) {
            this.loadingGetData = true;
            await this.fetch(page);
            this.loadingGetData = false;
        }
    }
};
</script>
